// packages/web/src/components/conversations/ConversationDrawer.tsx
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ConversationListPanel } from '@/components/conversations/ConversationListPanel';
import { useClickOutside } from '@/hooks/useClickOutside';

interface ConversationDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Drag left past this many px and release to dismiss the drawer.
const SWIPE_CLOSE_THRESHOLD_PX = 60;

export function ConversationDrawer({
  open,
  onClose,
}: ConversationDrawerProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);
  // Live leftward drag offset (<= 0) for swipe-to-dismiss feedback.
  const [dragX, setDragX] = useState(0);
  useClickOutside(panelRef, onClose);

  // Escape closes the drawer (parity with desktop dialogs; the backdrop tap is
  // handled by useClickOutside).
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  // Reset the swipe offset whenever the drawer closes. `if (!open) return null`
  // below does NOT unmount the instance, so without this a mid-drag close (or a
  // close via Esc/backdrop while partially swiped) would leave dragX non-zero
  // and the panel pre-translated off-screen on the next open.
  useEffect(() => {
    if (!open) {
      setDragX(0);
      touchStartXRef.current = null;
    }
  }, [open]);

  if (!open) return null;

  function handleTouchStart(e: React.TouchEvent): void {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  }

  function handleTouchMove(e: React.TouchEvent): void {
    if (touchStartXRef.current === null) return;
    const delta = (e.touches[0]?.clientX ?? 0) - touchStartXRef.current;
    // Only track leftward drag (toward dismissal); ignore rightward pull.
    setDragX(Math.min(0, delta));
  }

  function handleTouchEnd(): void {
    const shouldClose = dragX <= -SWIPE_CLOSE_THRESHOLD_PX;
    // Always reset first so a successful-swipe close also starts clean on reopen
    // (the close branch must not leave dragX at its dragged value).
    setDragX(0);
    touchStartXRef.current = null;
    if (shouldClose) onClose();
  }

  // touchcancel (system interruption: incoming call, notification, scroll
  // seizure) never fires touchend — without this the panel would stay translated
  // with a stale start ref. Snap back, don't close.
  function handleTouchCancel(): void {
    setDragX(0);
    touchStartXRef.current = null;
  }

  const dragging = dragX !== 0;

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />
      {/* Slide-over panel */}
      <div
        ref={panelRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{ transform: dragX ? `translateX(${dragX}px)` : undefined }}
        className={`absolute left-0 top-0 bottom-0 flex w-[85%] max-w-[340px] flex-col border-r border-border bg-surface shadow-xl ${
          dragging ? '' : 'transition-transform'
        }`}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold text-text-primary">Conversations</span>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-text-tertiary hover:bg-surface-elevated hover:text-text-primary"
            aria-label="Close conversations"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <ConversationListPanel onNavigate={onClose} />
        </div>
      </div>
    </div>
  );
}
